/** Key Results — edit form (RHF + zod + shadcn Form via Refine useForm). */
import { useForm } from "@refinedev/react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { Control, FieldValues, Path } from "react-hook-form";

import {
  updateKeyResultSchema,
  ENTITY_STATUS,
  type KeyResult,
  type UpdateKeyResultInput,
} from "@aos/shared";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import { PageHeader, LoadingState, ErrorState } from "../projects/_shared";
import { FormGrid, SelectField, TextField } from "../projects/_form";

/** Coerce an empty number input to undefined; otherwise to a finite number. */
function numberOrUndefined(v: string): number | undefined {
  if (v === "" || v == null) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Numeric field that coerces an empty input to `undefined` (never submits NaN —
 * design-system §4.5). Inline because the shared `TextField` doesn't take a
 * `setValueAs` and the shared `_form.tsx` is a primitive we must not edit.
 */
function NumberField<TValues extends FieldValues>({
  control,
  name,
  label,
}: {
  control: Control<TValues>;
  name: Path<TValues>;
  label: string;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Input
              type="number"
              step="any"
              name={field.name}
              ref={field.ref}
              onBlur={field.onBlur}
              value={field.value ?? ""}
              onChange={(e) => field.onChange(numberOrUndefined(e.target.value))}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

/**
 * KeyResultEdit (D6) — edit form for key results. Updating `current_value`
 * here is how OKR progress advances (spec §13.3 / §23 OKRs KeyResultEdit).
 */
export function KeyResultEdit() {
  const form = useForm<KeyResult, any, UpdateKeyResultInput>({
    resolver: zodResolver(updateKeyResultSchema),
    refineCoreProps: { resource: "key-results", action: "edit" },
  });

  const {
    refineCore: { formLoading, query },
    saveButtonProps,
    control,
  } = form;

  if (query?.isLoading) {
    return <LoadingState label="Loading key result…" />;
  }
  if (query?.isError) {
    return <ErrorState error={query.error} />;
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <PageHeader
        title="Edit Key Result"
        description="Update the current value to advance progress, or adjust the target."
      />
      <Form {...form}>
        <form onSubmit={saveButtonProps.onClick}>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Key result details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <TextField control={control} name="title" label="Title" />
              <FormGrid>
                <NumberField
                  control={control}
                  name="current_value"
                  label="Current value"
                />
                <NumberField
                  control={control}
                  name="target_value"
                  label="Target value"
                />
                <TextField control={control} name="unit" label="Unit" />
                <SelectField
                  control={control}
                  name="status"
                  label="Status"
                  options={ENTITY_STATUS}
                />
              </FormGrid>
            </CardContent>
            <CardFooter className="justify-end">
              <Button type="submit" disabled={formLoading}>
                {formLoading ? "Saving…" : "Save Changes"}
              </Button>
            </CardFooter>
          </Card>
        </form>
      </Form>
    </div>
  );
}

export default KeyResultEdit;
